import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { YStack, Text, Spinner } from "tamagui";
import { AxiosResponse } from "axios";
import api, { handleRequest } from "@services/axios/apiManager";
import { colors } from "@styles/colors";
import { useLineAuth } from "@utils/store/auth/useLineAuth";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { useAuthReady } from "@provider/LineLoginProvider";
import appToast from "@components/Toast";
import {
  getSmsInvite,
  clearSmsInvite,
  getSmsInviteSubmitLockRemainingMs,
  lockSmsInviteSubmit,
  unlockSmsInviteSubmit,
} from "@utils/smsInvite";
import { trackGa } from "@lib/tracker";
import { rememberPreferredContext } from "@utils/line/preferredContext";

/** 後端 /app-api/line/register-by-sms-invite 回傳結構。
 *  context_id：邀請碼綁新瓦斯行開新身分案（docs/2026-09-03-invite-code-supplier-context-design.md）新增，
 *  舊後端不帶則為 undefined，行為不變。 */
interface RegisterBySmsInviteResult {
  jwtToken?: string;
  order_id?: string | null;
  cis_id?: string | null;
  supplier_id?: string | null;
  context_id?: number | null;
}

/** 找不到序號註冊頁時的保底導向。 */
const REGISTER_FALLBACK = "/register/step2/inviteCode";

/**
 * 簡訊邀請落地頁。
 *
 * 使用者從瓦斯行簡訊點進 LIFF 連結（帶 phone / invite / order_id），
 * 全程只看到 loading，不需填任何表單：
 *   1. 等 LINE access token 就緒（由 LineLoginProvider 完成 liff 登入後注入）。
 *   2. 讀 sessionStorage 內攜帶的簡訊邀請參數。
 *   3. 呼叫後端靜默註冊 + 綁定，取得客戶 jwtToken。
 *   4. 成功 → 寫入 token，直達訂單詳情；訂單尚未建立則落在「訂單建立中」空狀態。
 *   5. 失敗（邀請碼失效等）→ 非既有客戶 toast 後導既有序號註冊頁；
 *      既有客戶（cusJwtToken 已有值，代表 LINE 帳號本來就註冊過）不導序號註冊頁，
 *      改溫和提示後回首頁，避免把老客戶誤導成「要重新註冊」。
 */
const SmsInviteLanding = () => {
  const navigate = useNavigate();
  const { lineAccessToken } = useLineAuth();
  const { cusJwtToken, setCusJwtToken } = useCusAuth();
  const authReady = useAuthReady();
  // 只允許送出一次，避免 token / re-render 造成重複註冊
  const submittedRef = useRef(false);
  // register-by-sms-invite 失敗時，不能立刻用 cusJwtToken 判斷「是否為既有客戶」——
  // lineAccessToken 就緒只代表 liff 拿到了 token，不代表 LineLoginProvider 的
  // /app-api/line/login（handleAppLogin）已經 resolve；若它還沒回來，cusJwtToken
  // 可能還是 null，會誤判成「非既有客戶」而把老客戶導去序號註冊頁。
  // 改成：失敗先記一個 flag，實際導頁交給下面的 effect，等 authReady（login 已塵埃落定，
  // 成功或 401 都會變 true）之後再依當時的 cusJwtToken 決定去哪。
  const [registerFailed, setRegisterFailed] = useState(false);
  // 被鎖早退時的重試計數器：鎖過期後 +1 觸發下面主 effect 重跑。
  // 保底機制——若先發請求的 instance 已消滅（頁面 reload / LIFF 重導向），
  // 鎖過期後不會有東西自動重新送出，使用者會卡在 spinner；靠這個 timeout 補上。
  const [retryTick, setRetryTick] = useState(0);
  // 一次性讀入 loadingText 的來源（src），避免 clearSmsInvite() 後 getSmsInvite() 讀不到值、
  // 文案短暫跳回預設「正在為您準備訂單…」再閃回去。
  const inviteSrcRef = useRef(getSmsInvite()?.src);

  useEffect(() => {
    // 尚未取得 LINE access token（Provider 仍在跑 liff 登入）→ 續等，維持 loading
    if (!lineAccessToken) return;
    if (submittedRef.current) return;

    // GA 漏斗（僅 GA、不進 app tracker）：進入簡訊邀請落地頁、拿到 LINE token 準備處理
    trackGa("funnel_step", { funnel: "sms_invite", step: "enter" });

    const invite = getSmsInvite();
    if (!invite) {
      // 缺參數（可能直接開網址或 sessionStorage 遺失）→ 導序號註冊頁
      trackGa("funnel_step", { funnel: "sms_invite", step: "error", reason: "missing_invite" });
      appToast.error("邀請連結資訊不完整，請重新開啟簡訊連結");
      clearSmsInvite();
      navigate(REGISTER_FALLBACK, { replace: true });
      return;
    }

    // sessionStorage 送出鎖：submittedRef 是 component instance 級的，LIFF / LINE login
    // 流程造成的 remount 會重置它，擋不住同一次點擊在極短時間內送出兩次相同請求。
    // 這裡改用跨 instance 存活的 sessionStorage 鎖（key 綁 invite_code），已有進行中的
    // 送出（30 秒內）就跳過，維持 loading 讓先發的請求自然完成導頁。
    const lockRemainingMs = getSmsInviteSubmitLockRemainingMs(invite.invite);
    if (lockRemainingMs > 0) {
      // 保底：若先發請求的 instance 已消滅（頁面 reload / LIFF 重導向），鎖過期後
      // 不會有東西自動重新送出。排一個 timeout，鎖到期後 +1 retryTick 讓本 effect 重跑；
      // 屆時鎖多半已過期，會走到下面正常送出。
      const timer = window.setTimeout(
        () => setRetryTick((tick) => tick + 1),
        lockRemainingMs + 500,
      );
      return () => window.clearTimeout(timer);
    }

    submittedRef.current = true;
    lockSmsInviteSubmit(invite.invite);
    // 靜默註冊 + 綁定送出
    trackGa("funnel_step", { funnel: "sms_invite", step: "register_submit" });

    handleRequest({
      asyncRequest: () =>
        api.post(
          "/app-api/line/register-by-sms-invite",
          {
            accessToken: lineAccessToken,
            phone: invite.phone,
            invite_code: invite.invite,
            // 簡訊連結帶的是 AW 端 order_list 的 id（ADO_xxxx）。後端據此補建
            // platform_order，並回傳新的 platform_order UUID 供導頁。
            order_id: invite.order_id || undefined,
          },
          { isAuth: false },
        ),
      successCallback: (res: AxiosResponse) => {
        unlockSmsInviteSubmit(invite.invite);
        const data = (res?.data?.data ?? {}) as RegisterBySmsInviteResult;
        if (data.jwtToken) {
          sessionStorage.setItem("jwtToken", data.jwtToken);
          setCusJwtToken(data.jwtToken);
        }
        clearSmsInvite();

        // 綁定成功時若回應帶 context_id（開新身分/認領既有帳號情境）→ 記一次性偏好身分，
        // 身分選單下次載入自動切過去；舊後端不帶則不動作，行為不變。
        rememberPreferredContext(data.context_id);

        // 導頁**只能**用後端回傳的 platform_order UUID。
        // 連結上的 invite.order_id 是 AW 端 order_list 的 id（ADO_xxxx），
        // 訂單詳情頁查的是 platform_order（UUID 主鍵），拿 ADO_xxxx 去查會直接錯。
        const platformOrderId = data.order_id || "";
        if (platformOrderId) {
          // 帶 pending 旗標：訂單若尚未同步到查詢端，詳情頁顯示「訂單建立中」而非彈回列表
          trackGa("funnel_step", { funnel: "sms_invite", step: "complete", result: "order_detail" });
          navigate(`/orders/${platformOrderId}`, { replace: true, state: { pending: true } });
        } else if (invite.src === "broadcast") {
          // 平台廣發邀請：本來就沒有訂單，註冊完成直接進首頁
          trackGa("funnel_step", { funnel: "sms_invite", step: "complete", result: "home" });
          navigate("/home", { replace: true });
        } else {
          // 後端未補建出訂單（掛斷觸發無訂單、或該單補建不成立）→「訂單建立中」空狀態
          trackGa("funnel_step", { funnel: "sms_invite", step: "complete", result: "order_pending" });
          navigate("/orders/pending", { replace: true });
        }
      },
      errorCallback: () => {
        unlockSmsInviteSubmit(invite.invite);
        trackGa("funnel_step", { funnel: "sms_invite", step: "error", reason: "register_failed" });
        clearSmsInvite();
        // 導頁決策交給下面的 effect：要等 LineLoginProvider 的 login 呼叫塵埃落定
        // （authReady）才知道 cusJwtToken 是否可信。
        setRegisterFailed(true);
      },
    });
  }, [lineAccessToken, navigate, setCusJwtToken, retryTick]);

  useEffect(() => {
    if (!registerFailed) return;
    // register-by-sms-invite 已失敗，但 LineLoginProvider 的 /app-api/line/login
    // 還沒 resolve（authReady 尚未 true）→ 繼續等，維持 loading，不要 busy-wait。
    if (!authReady) return;

    // 既有客戶（LINE 帳號本來就已註冊）：這次呼叫只是想順便綁定發簡訊的瓦斯行，
    // 失敗不影響其既有身分，不可再把他丟去序號註冊頁；溫和提示後回首頁即可。
    if (cusJwtToken) {
      appToast.error("綁定未完成，稍後可再從簡訊連結重試");
      navigate("/home", { replace: true });
      return;
    }
    // 非既有客戶：邀請碼失效 / 其他錯誤 → 提示後導既有序號註冊頁
    appToast.error("邀請連結已失效，請改用序號註冊或聯絡瓦斯行");
    navigate(REGISTER_FALLBACK, { replace: true });
  }, [registerFailed, authReady, cusJwtToken, navigate]);

  const loadingText = inviteSrcRef.current === "broadcast" ? "正在為您完成註冊…" : "正在為您準備訂單…";

  return (
    <YStack f={1} width="100vw" height="100dvh" ai="center" jc="center" bg={colors.bg} gap="$4">
      <Spinner size="large" color={colors.primary} />
      <Text fontSize={14} color={colors.gray}>
        {loadingText}
      </Text>
    </YStack>
  );
};

export default SmsInviteLanding;
